import { LANGUAGE_OPTIONS } from './languageOptions';
import { getCachedDictionaryFallbackChain } from '../locales';
import { translateWithFallback } from '../lib/translate';
import { ONBOARDING_FLOW_OVERRIDES } from './onboardingFlowOverrides';

/** Language-picker headline per locale (shown in header + below Mingo logo). */
export const LANGUAGE_PICK_PROMPTS = {
  ko: '어떤 언어를 사용하세요?',
  en: 'Which language do you use?',
  vi: 'Bạn dùng ngôn ngữ nào?',
  ru: 'Какой язык вы используете?',
  ja: 'どの言語を使用しますか？',
  'zh-CN': '您使用哪种语言？',
  th: 'คุณใช้ภาษาอะไร?',
  id: 'Bahasa apa yang Anda gunakan?',
  hi: 'आप कौन सी भाषा का प्रयोग करते हैं?',
  'zh-TW': '您使用哪種語言？',
  ar: 'ما هي لغتك؟',
  fr: 'Quelle est votre langue ?',
  de: 'Was ist Ihre Sprache?',
  es: '¿Cuál es tu idioma?',
  pt: 'Qual é o seu idioma?',
  it: 'Qual è la tua lingua?',
  nl: 'Wat is uw taal?',
  pl: 'Jaki jest Twój język?',
  tr: 'Diliniz nedir?',
  uk: 'Яка ваша мова?',
  mn: 'Таны хэл аль вэ?',
  kk: 'Сіздің тіліңіз қандай?',
  uz: 'Tilingiz qaysi?',
  ky: 'Сиздин тилиңиз кайсы?',
  ms: 'Apakah bahasa anda?',
  fil: 'Ano ang iyong wika?',
  km: 'តើភាសារបស់អ្នកជាអ្វី?',
  my: 'သင့်ဘာသာစကားကဘာလဲ?',
  lo: 'ພາສາຂອງທ່ານແມ່ນຫຍັງ?',
  ne: 'तपाईंको भाषा के हो?',
  si: 'ඔබේ භාෂාව කුමක්ද?',
  bn: 'আপনার ভাষা কী?',
  ur: 'آپ کی زبان کیا ہے؟',
  sw: 'Lugha yako ni nani?',
};

export function getLanguagePickPrompt(code) {
  if (!code) return LANGUAGE_PICK_PROMPTS.ko;
  return LANGUAGE_PICK_PROMPTS[code] || LANGUAGE_PICK_PROMPTS.ko;
}

/** All prompts joined (e.g. accessibility or future multi-line preview). */
export function getMultilingualLanguageTitles() {
  return LANGUAGE_OPTIONS.map(({ code }) => getLanguagePickPrompt(code));
}

export function formatMultilingualLine(parts, separator = ' / ') {
  return parts.filter(Boolean).join(separator);
}

/** PWA install banner — synced with onboarding language preview. */
export const PWA_INSTALL_COPY = {
  ko: {
    bannerAria: '앱 설치 안내',
    bannerTitle: '홈 화면에 Mingo 추가하기',
    iosHint: '공유 버튼 → 홈 화면에 추가',
    androidHint: '아래 설치를 누르거나 브라우저 메뉴에서 앱 설치를 선택하세요.',
    install: '설치',
    close: '닫기',
  },
  en: {
    bannerAria: 'Install app',
    bannerTitle: 'Add Mingo to your home screen',
    iosHint: 'Tap Share, then “Add to Home Screen”.',
    androidHint: 'Tap Install below, or use your browser menu → Install app.',
    install: 'Install',
    close: 'Close',
  },
  vi: {
    bannerAria: 'Cài đặt ứng dụng',
    bannerTitle: 'Thêm Mingo vào màn hình chính',
    iosHint: 'Nhấn Chia sẻ, sau đó “Thêm vào màn hình chính”.',
    androidHint: 'Nhấn Cài đặt bên dưới hoặc dùng menu trình duyệt → Cài đặt ứng dụng.',
    install: 'Cài đặt',
    close: 'Đóng',
  },
  ru: {
    bannerAria: 'Установить приложение',
    bannerTitle: 'Добавьте Mingo на главный экран',
    iosHint: 'Нажмите «Поделиться», затем «Добавить на главный экран».',
    androidHint: 'Нажмите «Установить» ниже или воспользуйтесь меню браузера → Установить приложение.',
    install: 'Установить',
    close: 'Закрыть',
  },
  ja: {
    bannerAria: 'アプリをインストール',
    bannerTitle: 'Mingoをホーム画面に追加',
    iosHint: '「共有」をタップし、「ホーム画面に追加」を選択。',
    androidHint: '下の「インストール」をタップするか、ブラウザメニュー → アプリをインストール。',
    install: 'インストール',
    close: '閉じる',
  },
  'zh-CN': {
    bannerAria: '安装应用',
    bannerTitle: '将 Mingo 添加到主屏幕',
    iosHint: '点击分享，然后选择“添加到主屏幕”。',
    androidHint: '点击下方安装，或使用浏览器菜单 → 安装应用。',
    install: '安装',
    close: '关闭',
  },
  th: {
    bannerAria: 'ติดตั้งแอป',
    bannerTitle: 'เพิ่ม Mingo ไปที่หน้าจอหลัก',
    iosHint: 'แตะแชร์ แล้วเลือก “เพิ่มไปที่หน้าจอหลัก”',
    androidHint: 'แตะติดตั้งด้านล่าง หรือใช้เมนูเบราว์เซอร์ → ติดตั้งแอป',
    install: 'ติดตั้ง',
    close: 'ปิด',
  },
  id: {
    bannerAria: 'Instal aplikasi',
    bannerTitle: 'Tambahkan Mingo ke layar utama',
    iosHint: 'Ketuk Bagikan, lalu “Tambahkan ke Layar Utama”.',
    androidHint: 'Ketuk Instal di bawah atau gunakan menu browser → Instal aplikasi.',
    install: 'Instal',
    close: 'Tutup',
  },
  hi: {
    bannerAria: 'ऐप इंस्टॉल करें',
    bannerTitle: 'Mingo को होम स्क्रीन पर जोड़ें',
    iosHint: 'शेयर पर टैप करें, फिर “होम स्क्रीन में जोड़ें”।',
    androidHint: 'नीचे इंस्टॉल पर टैप करें, या ब्राउज़र मेनू → ऐप इंस्टॉल करें।',
    install: 'इंस्टॉल',
    close: 'बंद करें',
  },
  'zh-TW': {
    bannerAria: '安裝應用程式',
    bannerTitle: '將 Mingo 新增到主畫面',
    iosHint: '點擊分享，然後選擇「加入主畫面」。',
    androidHint: '點擊下方安裝，或使用瀏覽器選單 → 安裝應用程式。',
    install: '安裝',
    close: '關閉',
  },
};

export function getPwaInstallCopy(code) {
  if (!code) return PWA_INSTALL_COPY.ko;
  return PWA_INSTALL_COPY[code] || PWA_INSTALL_COPY.ko;
}

/** Signup/onboarding screens — uses saved UI language + locale overrides. */
export function onboardingT(code, key, params) {
  const locale = code || 'ko';
  const override = ONBOARDING_FLOW_OVERRIDES[locale]?.[key];
  if (override) {
    if (!params) return override;
    return Object.entries(params).reduce(
      (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
      override,
    );
  }
  return translateWithFallback(getCachedDictionaryFallbackChain(locale), key, params);
}
